/**
 * Normalizes a value for use with v-select or similar components.
 * Returns undefined if the value is 0, null, or undefined,
 * otherwise returns the original value. 
 * 
 * This is useful for handling cases where a value of 0 is not valid
 * or should be treated as a non-selection in a dropdown.
 * 
 * @param {*} value The value to normalize.
 * @returns {*} The original value or undefined.
 */
export function normalizeSelectModel(value) {
  if (value === 0 || value === null || typeof value === 'undefined') {
    return undefined;
  }
  return value;
}

/**
 * Recursively normalizes a node and its children for use with vue-treeselect options. 
 * This can come handy in areas like user's profile.
 * @param {*} node The original node object
 * @param {string} [idProp='id'] The name of the property holding the ID in the original node (defaults to 'id').
 * @param {string} [labelProp='name']  The name of the property holding the label in the original node (defaults to 'name').
 * @param {string} [childrenProp='children'] The name of the property holding the children in the original node (defaults to 'children').
 * @returns {object | null} The normalized node object with normalized children, or null if the input node is falsy.
 */
export function normalizeTreeselectNode(node, idProp = 'id', labelProp = 'name', childrenProp = 'children') {
  if (!node) {
    return null;
  }

  const idValue = node[idProp];
  let normalizedChildren;

  if (node[childrenProp] && Array.isArray(node[childrenProp])) {
    // Recursively normalize each child node.
    normalizedChildren = node[childrenProp]
      .map(child => normalizeTreeselectNode(child, idProp, labelProp, childrenProp))
      .filter(child => child !== null); // Remove any null children that might result from invalid child nodes

    // If, after normalization, there are no valid children, set to undefined.
    // vue-treeselect typically hides the expander icon if children is undefined.
    if (normalizedChildren.length === 0) {
      normalizedChildren = undefined;
    }
  } else {
    // If the node has no children property or it's not an array,
    // set normalizedChildren to undefined.
    normalizedChildren = undefined;
  }

  return {
    id: idValue,
    label: node[labelProp] || `(Id: ${idValue} - Label missing)`,
    // Assign the (now normalized) children to the 'children' property.
    children: normalizedChildren,
  };
}